import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Sparkles, Eye, EyeOff, CheckCircle,
  AlertTriangle, Loader2, FileText,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet, apiPatch } from '@/lib/api'
import { cn } from '@/lib/utils'

interface AISettings {
  groqApiKey: string
  hasGroqKey: boolean
  source: { groq: string }
}

export function SettingsPage() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const isAdmin = user?.role === 'GROUP_ADMIN'

  const { data: aiSettings, isLoading } = useQuery({
    queryKey: ['ai-settings'],
    queryFn: () => apiGet<AISettings>('/settings/ai'),
  })

  // Form state
  const [groqKey, setGroqKey] = useState('')
  const [showGroqKey, setShowGroqKey] = useState(false)
  const [saved, setSaved] = useState(false)

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPatch('/settings/ai', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] })
      setGroqKey('')
      setShowGroqKey(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  function handleSave() {
    const body: Record<string, unknown> = {}
    if (groqKey) body.groqApiKey = groqKey
    saveMutation.mutate(body)
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-navy-800">Settings</h1>
        <p className="text-gray-500 text-sm mt-0.5">Manage platform configuration</p>
      </div>

      {/* DocFlow Configuration — primary */}
      <DocFlowSettings isAdmin={isAdmin} />

      {/* AI Triage Configuration */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-navy-800">AI Triage</h2>
            <p className="text-sm text-gray-500">Work order triage uses a local AI provider for speed. Chat routes through DocFlow.</p>
          </div>
          {aiSettings?.hasGroqKey ? (
            <span className="badge bg-green-100 text-green-800 ml-auto">Connected</span>
          ) : (
            <span className="badge bg-gray-100 text-gray-500 ml-auto">Not configured</span>
          )}
        </div>

        {isLoading ? (
          <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
        ) : isAdmin ? (
          <div className="space-y-4">
            <div>
              <label className="label">Groq API Key <span className="text-gray-400 font-normal">(for work order triage)</span></label>
              <div className="relative">
                <input
                  type={showGroqKey ? 'text' : 'password'}
                  value={groqKey}
                  onChange={(e) => setGroqKey(e.target.value)}
                  className="input pr-10 font-mono text-sm"
                  placeholder={aiSettings?.groqApiKey || 'gsk_...'}
                />
                <button
                  type="button"
                  onClick={() => setShowGroqKey(!showGroqKey)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {showGroqKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Get your key from <a href="https://console.groq.com/keys" target="_blank" rel="noopener noreferrer" className="text-navy-600 hover:underline">console.groq.com/keys</a>. Used for fast JSON-mode triage only.
              </p>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div>
                {saved && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Saved
                  </span>
                )}
              </div>
              <button onClick={handleSave} disabled={saveMutation.isPending} className="btn-primary">
                {saveMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : 'Save AI Settings'}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Only Group Administrators can configure AI settings.</p>
        )}
      </div>
    </div>
  )
}

// ── DocFlow Settings Component ──────────────────────────────────────────────
function DocFlowSettings({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient()
  const [baseUrl, setBaseUrl] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [siteIds, setSiteIds] = useState<string[]>([])
  const [showPassword, setShowPassword] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)
  const [saved, setSaved] = useState(false)

  const { data: dfSettings, isLoading } = useQuery({
    queryKey: ['docflow-settings'],
    queryFn: () => apiGet<{ baseUrl: string; apiKey: string; hasApiKey: boolean; serviceEmail: string; authMethod: string; siteId: string; siteIds: string[]; isConfigured: boolean; source: string }>('/settings/docflow'),
  })

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPatch('/settings/docflow', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docflow-settings'] })
      setPassword('')
      setApiKey('')
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  function handleSave() {
    saveMutation.mutate({
      ...(baseUrl && { baseUrl }),
      ...(apiKey && { apiKey }),
      ...(email && { serviceEmail: email }),
      ...(password && { servicePassword: password }),
      ...(siteIds.length && { siteIds, siteId: siteIds[0] }),
    })
  }

  return (
    <div className="card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
          <FileText className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-navy-800">DocFlow Integration</h2>
          <p className="text-sm text-gray-500">Connect to DocFlow document management</p>
        </div>
        {dfSettings?.isConfigured ? (
          <span className="badge bg-green-100 text-green-800 ml-auto">
            Connected {dfSettings.authMethod === 'api_key' ? '(API Key)' : '(Email)'}
          </span>
        ) : dfSettings?.authMethod !== 'none' && !dfSettings?.siteIds?.length ? (
          <span className="badge bg-amber-100 text-amber-800 ml-auto">No site selected</span>
        ) : (
          <span className="badge bg-gray-100 text-gray-500 ml-auto">Not configured</span>
        )}
      </div>

      {isLoading ? (
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
      ) : isAdmin ? (
        <div className="space-y-4">
          <div>
            <label className="label">DocFlow Base URL</label>
            <input
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              className="input font-mono text-sm"
              placeholder={dfSettings?.baseUrl || 'https://api.docflow.corekey.app'}
            />
          </div>
          <div>
            <label className="label">API Key <span className="text-gray-400 font-normal">(recommended — dfk_...)</span></label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="input pr-10 font-mono text-sm"
                placeholder={dfSettings?.hasApiKey ? '••••••••••••••••' : 'dfk_...'}
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {dfSettings?.hasApiKey && (
              <p className="text-xs text-green-600 mt-1">API key configured</p>
            )}
          </div>
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs text-gray-400 mb-3">Or use legacy email/password authentication:</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Service Email</label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input text-sm"
                  placeholder={dfSettings?.serviceEmail || 'admin@mastercopy.co.uk'}
                />
              </div>
              <div>
                <label className="label">Service Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pr-10 text-sm"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <DocFlowSitePicker siteIds={siteIds.length ? siteIds : (dfSettings?.siteIds ?? [])} onSelect={setSiteIds} />

          <div className="flex items-center justify-between pt-2">
            <div>
              {saved && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> Saved
                </span>
              )}
            </div>
            <button onClick={handleSave} disabled={saveMutation.isPending} className="btn-primary">
              {saveMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : 'Save DocFlow Settings'}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Only Group Administrators can configure DocFlow.</p>
      )}
    </div>
  )
}

// ── DocFlow Site Picker (multi-select) ──────────────────────────────────────
function DocFlowSitePicker({ siteIds, onSelect }: { siteIds: string[]; onSelect: (ids: string[]) => void }) {
  const { data: sites, isLoading, refetch } = useQuery({
    queryKey: ['docflow-sites'],
    queryFn: () => apiGet<{ id: string; name: string; description: string | null }[]>('/docflow/sites'),
    enabled: false,
  })

  function toggleSite(id: string) {
    if (siteIds.includes(id)) {
      onSelect(siteIds.filter(s => s !== id))
    } else {
      onSelect([...siteIds, id])
    }
  }

  return (
    <div>
      <label className="label">DocFlow Sites</label>
      {sites && sites.length > 0 ? (
        <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-60 overflow-y-auto">
          {sites.map(s => (
            <label key={s.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={siteIds.includes(s.id)}
                onChange={() => toggleSite(s.id)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-800">{s.name}</span>
                {s.description && <span className="text-xs text-gray-400 ml-2">{s.description}</span>}
              </div>
            </label>
          ))}
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            value={siteIds.join(', ')}
            readOnly
            className="input flex-1 font-mono text-sm"
            placeholder="Click Fetch Sites to load available sites"
          />
        </div>
      )}
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-gray-400">
          {siteIds.length > 0
            ? `${siteIds.length} site${siteIds.length > 1 ? 's' : ''} connected`
            : <span className="text-red-500 font-medium">Select at least one site to enable DocFlow access.</span>
          }
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isLoading}
          className="btn-secondary text-xs"
        >
          {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Fetch Sites
        </button>
      </div>
    </div>
  )
}
